// src/utils/importMappers.ts
// Transforms raw CSV/DB rows into the canonical frontend item shapes.
// Handles both snake_case DB exports and camelCase API exports.

import type { SparkPlugItem, SensorItem, IgnitionCoilItem, SteeringClockItem, WiresItem } from '../types/ProductItems';
import { STATIC_ORIGIN } from '../config';

// ─── helpers ────────────────────────────────────────────────────────────────

const parseImgsAll = (raw: unknown): string[] =>
  (raw != null && raw !== '' ? String(raw) : '')
    .split('|')
    .filter(Boolean)
    .map((p) => `${STATIC_ORIGIN}/${p}`);

const str = (v: unknown): string =>
  v != null && v !== '' ? String(v).trim() : '';

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Appends "мм" if the value is a bare number string (e.g. "16" → "16мм").
const withMm = (v: unknown): string => {
  const s = str(v);
  return s !== '' && !s.endsWith('мм') ? `${s}мм` : s;
};

// Parses GROUP_CONCAT output "KEY:value1|KEY:value2|OTHER:value3"
// into a grouped Record<string, string[]> used by references/oems fields.
// Separator "|" and delimiter ":" are safe: brand/model names and part codes
// never contain these characters.
function parseGrouped(raw: unknown): Record<string, string[]> | undefined {
  const s = str(raw);
  if (!s) return undefined;
  const result: Record<string, string[]> = {};
  for (const entry of s.split('|')) {
    const colonIdx = entry.indexOf(':');
    if (colonIdx === -1) continue;
    const key = entry.slice(0, colonIdx).trim();
    const val = entry.slice(colonIdx + 1).trim();
    if (!key || !val) continue;
    (result[key] ??= []).push(val);
  }
  return Object.keys(result).length ? result : undefined;
}

// ─── ignition coils ──────────────────────────────────────────────────────────

export function mapToIgnitionCoil(row: Record<string, unknown>): IgnitionCoilItem {
  const id = str(row.product_id ?? row.id) || crypto.randomUUID();

  const hasPriceOriginal = num(row.price_original) > 0;
  const hasPriceCopy     = num(row.price_copy)     > 0;
  const original: 'original' | 'copy' =
    str(row.original) === 'original' || (hasPriceOriginal && !hasPriceCopy)
      ? 'original'
      : 'copy';

  return {
    id,
    key: id,
    brand:          str(row.brand),
    partNumber:     str(row.partNumber    ?? row.detail_number),
    sku:            str(row.sku),
    model:          str(row.model),
    price:          num(row.price         ?? row.price_copy    ?? row.price_original),
    quantity:       num(row.quantity      ?? row.count_copy    ?? row.count_original),
    connectorCount: num(row.connectorCount ?? row.plugs_number),
    contactCount:   num(row.contactCount  ?? row.contacts_number),
    original,
    references: parseGrouped(row.refs_raw),
    oems:       parseGrouped(row.oems_raw),
    img:        str(row.img),
    images:     parseImgsAll(row.imgs_all),
  };
}

// ─── sensors (crankshaft / camshaft) ─────────────────────────────────────────

export function mapToSensor(row: Record<string, unknown>): SensorItem {
  // wired: 0 = беспроводной, 1 = проводной
  const rawWired = row.wired ?? row.connectionType;
  let connectionType: string;
  if (rawWired === 'wired' || rawWired === 'wireless') {
    connectionType = String(rawWired);
  } else {
    connectionType = Number(rawWired) === 1 ? 'wired' : 'wireless';
  }

  const id = str(row.product_id ?? row.id) || crypto.randomUUID();

  const hasPriceOriginal = num(row.price_original) > 0;
  const hasPriceCopy     = num(row.price_copy)     > 0;
  const original: 'original' | 'copy' =
    str(row.original) === 'original' || (hasPriceOriginal && !hasPriceCopy)
      ? 'original'
      : 'copy';

  return {
    id,
    key: id,
    brand:          str(row.brand),
    partNumber:     str(row.partNumber  ?? row.detail_number),
    sku:            str(row.sku),
    model:          str(row.model),
    price:          num(row.price       ?? row.price_copy    ?? row.price_original),
    quantity:       num(row.quantity    ?? row.count_copy    ?? row.count_original),
    sensorType:     str(row.sensorType  ?? row.sensor_type),
    connectionType,
    connectorType:  str(row.connectorType ?? row.connection_type),
    contactsCount:  num(row.contactsCount ?? row.contact_number),
    categoryName:   str(row.categoryName  ?? row.category_name),
    original,
    references: parseGrouped(row.refs_raw),
    oems:       parseGrouped(row.oems_raw),
    img:        str(row.img),
    images:     parseImgsAll(row.imgs_all),
  };
}

// ─── steering clocks (airbag cables) ─────────────────────────────────────────

export function mapToSteeringClock(row: Record<string, unknown>): SteeringClockItem {
  const id = str(row.product_id ?? row.id) || crypto.randomUUID();

  const hasPriceOriginal = num(row.price_original) > 0;
  const hasPriceCopy     = num(row.price_copy)     > 0;
  const original: 'original' | 'copy' =
    str(row.original) === 'original' || (hasPriceOriginal && !hasPriceCopy)
      ? 'original'
      : 'copy';

  return {
    id,
    key: id,
    brand:           str(row.brand),
    partNumber:      str(row.partNumber ?? row.detail_number),
    sku:             str(row.sku),
    model:           str(row.model),
    price:           num(row.price      ?? row.price_copy    ?? row.price_original),
    quantity:        num(row.quantity   ?? row.count_copy    ?? row.count_original),
    connectorCount:  num(row.connectorCount ?? row.steering_axle_bore_diameter),
    airbagWireCount: num(row.airbagWireCount ?? row.airbag_plugs_number),
    original,
    references: parseGrouped(row.refs_raw),
    oems:       parseGrouped(row.oems_raw),
    img:        str(row.img),
    images:     parseImgsAll(row.imgs_all),
  };
}

// ─── wires / ignition coil mouthpieces (наконечники) ─────────────────────────

export function mapToWires(row: Record<string, unknown>): WiresItem {
  const id = str(row.product_id ?? row.id) || crypto.randomUUID();

  const hasPriceOriginal = num(row.price_original) > 0;
  const hasPriceCopy     = num(row.price_copy)     > 0;
  const original: 'original' | 'copy' =
    str(row.original) === 'original' || (hasPriceOriginal && !hasPriceCopy)
      ? 'original'
      : 'copy';

  // type_ and contact_type are DB enum fields; API may alias them or pass as-is.
  const tipType = str(row.tipType ?? row.type_ ?? row.contact_type);

  return {
    id,
    key: id,
    brand:      str(row.brand),
    partNumber: str(row.partNumber ?? row.detail_number),
    sku:        str(row.sku),
    model:      str(row.model),
    price:      num(row.price      ?? row.price_copy    ?? row.price_original),
    quantity:   num(row.quantity   ?? row.count_copy    ?? row.count_original),
    tipType,
    length:     (() => { const v = parseFloat(String(row.length ?? '').replace(',', '.')); return Number.isFinite(v) && v > 0 ? v : undefined; })(),
    original,
    references: parseGrouped(row.refs_raw),
    oems:       parseGrouped(row.oems_raw),
    img:        str(row.img),
    images:     parseImgsAll(row.imgs_all),
  };
}

// ─── spark plugs ─────────────────────────────────────────────────────────────

// DB stores "шайбовый"/"конический"; the form/frontend uses "шайба"/"конус".
const SEAT_TYPE_MAP: Record<string, string> = {
  шайбовый: 'шайба',
  конический: 'конус',
};

export function mapToSparkPlug(row: Record<string, unknown>): SparkPlugItem {
  const rawSeat = str(row.seat_type ?? row.seatType);
  const seatType = SEAT_TYPE_MAP[rawSeat.toLowerCase()] ?? rawSeat;

  // Derive "original"/"copy" from explicit field or from which price column is set.
  const hasPriceOriginal = num(row.price_original) > 0;
  const hasPriceCopy     = num(row.price_copy)     > 0;
  const original: 'original' | 'copy' =
    str(row.original) === 'original' || (hasPriceOriginal && !hasPriceCopy)
      ? 'original'
      : 'copy';

  // product_id is the real business key that links spark_plugs → products.
  // Use it as the IndexedDB id so a future products-table import can enrich these records.
  const id = str(row.product_id ?? row.id) || crypto.randomUUID();

  return {
    id,
    key: id,
    brand:          str(row.brand),
    partNumber:     str(row.partNumber   ?? row.detail_number),
    sku:            str(row.sku),
    model:          str(row.model),
    price:          num(row.price        ?? row.price_copy    ?? row.price_original),
    quantity:       num(row.quantity     ?? row.count_copy    ?? row.count_original),
    electrodeType:  str(row.electrode_type  ?? row.electrodeType),
    electrodeCount: num(row.electrodes_number ?? row.electrodeCount ?? row.electrodesNumber) || 1,
    hexType:        str(row.key_type   ?? row.hexType  ?? row.keyType),
    hexSize:        withMm(row.key_size  ?? row.hexSize  ?? row.keySize),
    seatType,
    threadSize:     withMm(row.thread_size   ?? row.threadSize),
    threadLength:   withMm(row.thread_length ?? row.threadLength),
    gap:            str(row.gap),
    heatRating:     str(row.heatRating ?? row.heat_rating),
    original,
    references: parseGrouped(row.refs_raw),
    oems:       parseGrouped(row.oems_raw),
    img:        str(row.img),
    images:     parseImgsAll(row.imgs_all),
  };
}
